import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet } from "react-router-dom";
import useAuthStore from "../store/auth.store";
import useWalletStore from "../store/wallet.store";
import { isPlayerAccount } from "../components/common/RequireNotPlayer";
import { PanelLeft, X } from "lucide-react";
import { TwoBats, BatBall, List as ListIcon, Stump, Players, Umpire, Stadium } from "../assets/icons/CricketIcons";
import Sidebar from "../components/cricket/Sidebar";
import GlobalNav from "../components/layout/GlobalNav";

// 2026-06-05 — Mirrors the desktop Sidebar menu, rendered horizontally
// on phones / tablets (< lg). Same labels, same icons, same routes —
// founder ask: mobile must look like the desktop UI.
const CRICKET_NAV = [
  { label: "Matches",      path: "matches",      Icon: TwoBats },
  { label: "Competitions", path: "competitions", Icon: BatBall },
  { label: "Tournaments",  path: "tournaments",  Icon: ListIcon },
  { label: "Teams",        path: "teams",        Icon: Stump },
  { label: "Players",      path: "players",      Icon: Players },
  { label: "Umpires",      path: "umpires",      Icon: Umpire },
  { label: "Stadiums",     path: "stadiums",     Icon: Stadium },
];
import WalletModalsHost from "../components/wallet/WalletModalsHost";
import TrialBanner from "../components/wallet/TrialBanner";
import FeedbackWidget from "../components/feedback/FeedbackWidget";
import ErrorBoundary from "../components/common/ErrorBoundary";
import { ToastHost } from "../components/common/ToastHost";
import KeyboardHelp from "../components/common/KeyboardHelp";
import VersionWatcher from "../components/common/VersionWatcher";

export default function CricketLayout() {
  // On tablet portrait (md, < lg) the sidebar is hidden by default to give the
  // page room. Operators tap the floating button to slide it in as an overlay.
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Player-only accounts can't enter /cricket/* (no scoring / teams / etc).
  const { user, isAuthenticated } = useAuthStore();
  const fetchBalance = useWalletStore((s) => s.fetchBalance);
  const initWalletEventListeners = useWalletStore((s) => s.initWalletEventListeners);
  // 2026-06-05 — wallet badge live-refresh. The scorecard page debits
  // ZC for AI Match Report + Match Report PDF; without this listener
  // the badge displayed stale numbers and operators reported "ZC not
  // deducted" even when the backend had charged correctly.
  useEffect(() => {
    if (!isAuthenticated) return;
    initWalletEventListeners();
    fetchBalance();
  }, [isAuthenticated, fetchBalance, initWalletEventListeners]);

  if (isPlayerAccount(user)) return <Navigate to="/me/player" replace />;

  return (
    <ToastHost>
    <div className="min-h-screen flex flex-col bg-bg-main text-text-primary">
      <GlobalNav />
      <TrialBanner />
      <div className="flex-1 flex">
        {/* Inline sidebar — only on lg+ desktop */}
        <Sidebar />

        {/* Tablet drawer — covers a small portion when toggled open */}
        {drawerOpen && (
          <div className="lg:hidden fixed inset-0 z-30" onClick={() => setDrawerOpen(false)}>
            <div className="absolute inset-0 bg-black/50" />
            <div
              className="absolute top-14 left-0 bottom-0 w-60 bg-bar border-r border-border-default shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-2 border-b border-border-default">
                <span className="text-[10px] uppercase tracking-[0.18em] text-text-secondary">Cricket data</span>
                <button onClick={() => setDrawerOpen(false)} className="text-text-secondary hover:text-text-primary">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <Sidebar forceVisible onItemClick={() => setDrawerOpen(false)} />
            </div>
          </div>
        )}

        <main className="flex-1 p-4 sm:p-6 min-w-0">
          {/* 2026-06-05 — mobile / tablet cricket nav strip. Replaces
              the "Cricket sections" drawer button so phones see the
              same labels + icons that the lg+ left sidebar shows,
              just laid out horizontally with a scroll. The drawer
              button is kept as a fallback so the old shortcut stays
              functional. */}
          <nav className="lg:hidden -mx-1 mb-3 overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
            <div className="inline-flex items-center gap-1 px-1 py-0.5 whitespace-nowrap">
              {CRICKET_NAV.map(({ label, path, Icon }) => (
                <NavLink
                  key={path}
                  to={`/cricket/${path}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      isActive
                        ? "bg-button-primary/15 border-button-primary text-text-primary"
                        : "bg-bg-primary border-border-default text-text-secondary hover:text-text-primary hover:border-button-primary/40"
                    }`
                  }
                >
                  <span className="inline-flex w-3.5 h-3.5"><Icon /></span>
                  {label}
                </NavLink>
              ))}
              <button
                onClick={() => setDrawerOpen(true)}
                title="Open sidebar drawer"
                className="ml-1 flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] uppercase tracking-wider bg-bg-secondary border border-border-default text-text-secondary hover:text-text-primary hover:border-button-primary/40 transition-colors"
              >
                <PanelLeft className="w-3 h-3" />
                More
              </button>
            </div>
          </nav>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      <WalletModalsHost />
      <FeedbackWidget />
      <KeyboardHelp />
      <VersionWatcher />
    </div>
    </ToastHost>
  );
}
